import { useEffect, useState } from "react";
import axios from "axios";
import { Button, Spinner } from "react-bootstrap";
import type { EpicRunParticipants } from "../../types/latestEpicRuns";
import { analytics } from "../../utils/analytics";
import PlayerLink from "../common/PlayerLink";
import "./RunParticipants.css";

// Who was on each run; a finished run's players don't change, so each run is
// fetched at most once per page load.
const participantsCache = new Map<number, string[]>();

type ParticipantsState =
  | { status: "loading" }
  | { status: "ok"; players: string[] }
  | { status: "error" };

const RunParticipants = ({ runId }: { runId: number }) => {
  const [state, setState] = useState<ParticipantsState>(() => {
    const hit = participantsCache.get(runId);
    return hit ? { status: "ok", players: hit } : { status: "loading" };
  });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const hit = participantsCache.get(runId);
    if (hit) {
      setState({ status: "ok", players: hit });
      return undefined;
    }

    const controller = new AbortController();
    setState({ status: "loading" });
    axios
      .get<EpicRunParticipants>(
        `${process.env.REACT_APP_API}public/epic-runs/${runId}/participants`,
        { signal: controller.signal }
      )
      .then((res) => {
        if (controller.signal.aborted) return;
        participantsCache.set(runId, res.data.players);
        setState({ status: "ok", players: res.data.players });
      })
      .catch((err) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        console.error("Failed to fetch epic run participants", err);
        analytics.trackError("epic_run_participants", String(err?.message));
        setState({ status: "error" });
      });

    return () => controller.abort();
  }, [runId, attempt]);

  return (
    <div className="run-participants">
      {state.status === "loading" && (
        <Spinner animation="border" size="sm" variant="light" />
      )}
      {state.status === "error" && (
        <>
          <span className="me-2">Couldn't load the players.</span>
          <Button
            size="sm"
            variant="outline-light"
            onClick={() => setAttempt((n) => n + 1)}
          >
            Retry
          </Button>
        </>
      )}
      {state.status === "ok" &&
        (state.players.length === 0 ? (
          <span className="run-participants-muted">No players recorded.</span>
        ) : (
          <ul className="run-players" aria-label="Players">
            {state.players.map((player) => (
              <li key={player}>
                <PlayerLink
                  className="epic-lb-player-link"
                  player={player}
                  tab="epics"
                />
              </li>
            ))}
          </ul>
        ))}
    </div>
  );
};

export default RunParticipants;
